import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TypographyProps,
  GridProps,
  Button,
  Stack,
  Chip,
  PaperProps,
  TextField,
  StackProps,
  Typography,
} from '@mui/material';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { TP_COLORS } from '../../../constants';
import { getAuthState } from '../../../store/AuthSlice';
import { adminGetUserById, getUsersState } from '../../../store/UserSlice';
import {
  adminGetSubmissionById,
  adminGetSubmissionQuestion,
  getSubmissionState,
  gradingSubmissionOpenEndedAnswer,
  ISubmissionQuestion,
  resetSubmission,
} from '../../../store/SubmissionSlice';
import {
  getDiagnosticBySubmissionId,
  getDiagnosticState,
  resetDiagnostic,
} from '../../../store/DiagnosticSlice';
import { useAppSelector } from '../../../hooks';
import {
  checkIsUser,
  getQuestionDifficultyChipColor,
  getShortName,
  getUrlConvertedString,
} from '../../../helper';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import SubmissionsJumboTron from './components/SubmissionsJumboTron';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import AdminAnswerListItem from './components/AdminAnswerListItem'; // From User Component
import QuestionGridBox from '../../UserPages/ManageSubmissionQuestions/components/QuestionGridBox'; // From User Component

// Styles
const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};

const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

const detailGridProps: GridProps = {
  xs: 12,
  sm: 12,
  md: 8,
  lg: 9,
};

const labelStyles: React.CSSProperties = {
  borderRadius: '4px',
  fontWeight: 600,
  marginRight: '5px',
};

const questionDetailContainerStyles: PaperProps['sx'] = {
  boxShadow: 'unset',
  padding: '15px',
};

const gradeSectionStyles: StackProps = {
  direction: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  style: {
    marginTop: '15px',
  },
};

const labelProps: TypographyProps = {
  fontWeight: 600,
  variant: 'subtitle2',
  style: { marginBottom: '10px' },
};

const ViewSubmissionQuestions: React.FC = () => {
  const params = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const submissionState = useAppSelector(getSubmissionState);
  const usersState = useAppSelector(getUsersState);
  const authState = useAppSelector(getAuthState);
  const diagnosticState = useAppSelector(getDiagnosticState);

  const _user = usersState.user;
  const _submission = submissionState.submission;
  const _quiz = _submission && _submission.quiz;
  const _question = submissionState.question;
  const { diagnostic } = diagnosticState;
  const topic = _quiz && _quiz.topic ? _quiz.topic : undefined;
  const quizSubTopics = (_quiz && _quiz.subTopics
    ? _quiz.subTopics
    : []
  ).filter((item) => item.subTopic);

  let subTopicText = '';
  let paperText = '';

  quizSubTopics.forEach((quizSubTopic, index) => {
    const subTopicName = quizSubTopic.subTopic?.name || '';
    const paperName = quizSubTopic.paper || '';

    if (!subTopicText.includes(subTopicName)) {
      if (index > 0) {
        subTopicText += ', ';
      }

      subTopicText += subTopicName;
    }

    if (!paperText.includes(paperName)) {
      if (index > 0) {
        paperText += ', ';
      }

      paperText += paperName;
    }
  });

  const isDiagnostic =
    topic &&
    topic.name === 'Diagnostic Challenge' &&
    topic.subject !== 'Biology';

  // LOCAL STATES
  const [selectedQuestion, setSelectedQuestion] = useState<
    ISubmissionQuestion | undefined
  >(undefined);
  const [openEndedScore, setOpenEndedScore] = useState(0);
  const [openEndedComment, setOpenEndedComment] = useState('');

  // USE EFFECT
  useEffect(() => {
    if (params.userId) {
      dispatch(adminGetUserById(params.userId));

      if (params.submissionId) {
        dispatch(adminGetSubmissionById(params.submissionId));

        dispatch(getDiagnosticBySubmissionId(params.submissionId));
      }
    }

    return () => {
      dispatch(resetSubmission());
      dispatch(resetDiagnostic());
    };
  }, []);

  useEffect(() => {
    if (
      params.submissionId &&
      _submission &&
      _submission.questions &&
      _submission.questions.length > 0
    ) {
      if (selectedQuestion === undefined) {
        const _firstQuestion = _submission.questions[0];

        setSelectedQuestion(_firstQuestion);

        dispatch(
          adminGetSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: _firstQuestion.question,
          })
        );
      }
    }
  }, [params.submissionId, submissionState.submission?._id]);

  useEffect(() => {
    if (
      selectedQuestion &&
      selectedQuestion.isOpenEnded &&
      selectedQuestion.openEnded
    ) {
      setOpenEndedComment(selectedQuestion.openEnded.comment || '');
      setOpenEndedScore(selectedQuestion.openEnded.score || 0);
    }
  }, [selectedQuestion]);

  // METHODS
  const handleQuestionChange = (question: ISubmissionQuestion) => {
    if (params.submissionId) {
      dispatch(
        adminGetSubmissionQuestion({
          submissionId: params.submissionId,
          questionId: question.question,
        })
      );
    }

    setSelectedQuestion(question);

    window.scrollTo({ top: 0 });
  };

  const handleBackQuestionChange = () => {
    if (selectedQuestion && _submission) {
      const question = _submission.questions[selectedQuestion.order - 1];

      if (params.submissionId && question) {
        dispatch(
          adminGetSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: question.question,
          })
        );
      }

      setSelectedQuestion(question);
    }
  };

  const handleNextQuestionChange = () => {
    if (selectedQuestion && _submission) {
      const question = _submission.questions[selectedQuestion.order + 1];

      if (params.submissionId && question) {
        dispatch(
          adminGetSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: question.question,
          })
        );
      }

      setSelectedQuestion(question);
    }
  };

  const handleGradingOpenEndedAnswer = () => {
    if (params.submissionId && selectedQuestion) {
      dispatch(
        gradingSubmissionOpenEndedAnswer({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
          score: selectedQuestion.openEnded?.answer
            ? openEndedScore
            : undefined,
          comment: openEndedComment || undefined,
        })
      );
    }
  };

  const checkIsGraded = () => {
    if (selectedQuestion && _submission && _submission.questions.length) {
      const question = _submission.questions.find(
        (questionItem) => questionItem.question === selectedQuestion.question
      );

      if (
        question &&
        typeof question?.openEnded?.score === 'number' &&
        question?.openEnded?.score >= 0
      ) {
        return true;
      }
    }
    return false;
  };

  let userName = '';
  let lastLogin = '-';
  let totalScore = '-';

  if (_submission && _submission.score >= 0 && _submission.totalScore >= 0) {
    totalScore = `${_submission.score
      .toString()
      .padStart(2, '0')} / ${_submission.totalScore
      .toString()
      .padStart(2, '0')}`;
  }

  if (_user) {
    userName = getShortName(
      _user.firstname,
      _user.lastname,
      !checkIsUser(authState.user)
    );

    if (_user.lastLogin) {
      lastLogin = moment(_user.lastLogin).format('YYYY-MM-DD hh:mm A');
    }
  }

  if (userName.trim() === '') {
    userName = '-';
  }

  const RENDER_DETAILED_REPORT_BUTTON = () => {
    if (diagnostic) {
      const subject = getUrlConvertedString(topic?.subject || '');
      const curriculum = getUrlConvertedString(topic?.curriculum || '');
      const subTopic = getUrlConvertedString(subTopicText);

      return (
        <Paper
          sx={{
            boxShadow: 'unset',
          }}
        >
          <Box
            sx={{
              p: '12px',
              mb: '10px',
              borderRadius: '7px',
              backgroundColor: 'unset',
            }}
          >
            <Button
              fullWidth
              color="primary"
              variant="contained"
              sx={{
                fontWeight: 600,
                maxWidth: { xs: 'unset', sm: '400px' },
              }}
              onClick={() => {
                navigate(
                  `${process.env.PUBLIC_URL}/dashboard/my-quizzes/diagnostic/${diagnostic.subTopicId}/${diagnostic._id}/${curriculum}_${subject}_${subTopic}_${diagnostic.subTopicId}`
                );
              }}
            >
              See Detailed Report
            </Button>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const QUESTION_DETAILS_VIEW = (
    <>
      <Paper style={{ boxShadow: 'unset' }}>
        <DetailViewCard
          title="Subject Level"
          titleProps={{ variant: 'caption', fontWeight: 500 }}
          descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
          containerProps={{
            sx: { backgroundColor: 'unset' },
          }}
          description={topic && topic.name ? topic.name : '-'}
        />
      </Paper>
      <Paper style={{ boxShadow: 'unset' }}>
        <DetailViewCard
          title="Papers"
          titleProps={{ variant: 'caption', fontWeight: 500 }}
          description={paperText}
          descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
          containerProps={{
            sx: { backgroundColor: 'unset' },
          }}
        />
      </Paper>
      {_submission && _submission.status === 'completed' && !isDiagnostic && (
        <Paper style={{ boxShadow: 'unset' }}>
          <DetailViewCard
            title="Score"
            titleProps={{ variant: 'caption', fontWeight: 500 }}
            description={totalScore}
            descriptionProps={{ variant: 'subtitle2', fontWeight: 600 }}
            containerProps={{
              sx: { backgroundColor: 'unset' },
            }}
          />
        </Paper>
      )}
      {_submission &&
        _submission.status === 'completed' &&
        isDiagnostic &&
        RENDER_DETAILED_REPORT_BUTTON()}
      {_submission &&
        _question &&
        _submission.questions &&
        _submission.questions.length > 0 && (
          <QuestionGridBox
            selectedQuestion={selectedQuestion}
            submission={_submission}
            question={_question}
            onQuestionChange={handleQuestionChange}
          />
        )}
    </>
  );

  const RENDER_MANAGE_QUESTION_VIEW = () => {
    if (
      !_question &&
      !selectedQuestion &&
      submissionState.status === 'loading'
    ) {
      return (
        <div>
          <BasicLoader />
        </div>
      );
    }

    if (selectedQuestion && _question) {
      const QUESTION_NAVIGATION_BUTTONS = _submission &&
        _submission.questions &&
        _submission.questions.length > 0 && (
          <div>
            {selectedQuestion && selectedQuestion.order > 0 && (
              <Button
                disableElevation
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: TP_COLORS.black,
                  fontWeight: 600,
                }}
                onClick={handleBackQuestionChange}
              >
                Back
              </Button>
            )}
            {selectedQuestion &&
              selectedQuestion.order < _submission.questions.length - 1 && (
                <Button
                  disableElevation
                  size="small"
                  variant="contained"
                  color="inherit"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    color: TP_COLORS.black,
                    ml: '10px',
                    fontWeight: 600,
                  }}
                  onClick={handleNextQuestionChange}
                >
                  Next
                </Button>
              )}
          </div>
        );
      const permutator = (answers) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = [] as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const permute = (options, item: any = []) => {
          if (options.length === 0) {
            result.push(item);
          } else {
            for (let i = 0; i < options.length; i += 1) {
              const current = options.slice();
              const next = current.splice(i, 1);
              permute(current.slice(), item.concat(next));
            }
          }
        };
        permute(answers);
        return result;
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const permutationsNumber = [] as any;
      const factorial = (n) => {
        if (n === 0 || n === 1) return 1;
        if (permutationsNumber[n] > 0) return permutationsNumber[n];
        permutationsNumber[n] = factorial(n - 1) * n;
        return permutationsNumber[n];
      };

      let _questionShuffled;
      if (_submission && _submission.shuffleFactor) {
        const selectedShuffle = permutator(_question.answers)[
          _submission.shuffleFactor % factorial(_question.answers.length)
        ];
        _questionShuffled = selectedShuffle.map((item, index) => ({
          ...item,
          order: index,
        }));
      } else {
        _questionShuffled = _question.answers;
      }
      return (
        <div>
          <Paper sx={{ ...questionDetailContainerStyles, mb: '15px' }}>
            {/* TOP LABELS AND BUTTONS */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center">
                <Chip
                  label={`Question ${(selectedQuestion.order + 1)
                    .toString()
                    .padStart(2, '0')}`}
                  style={labelStyles}
                />
                <Chip
                  label={`${_question.marks.toString().padStart(2, '0')} Marks`}
                  style={labelStyles}
                />
                <Chip
                  label={_question.difficulty}
                  style={labelStyles}
                  color={getQuestionDifficultyChipColor(_question.difficulty)}
                />
                {selectedQuestion.isOpenEnded &&
                  selectedQuestion.openEnded?.answer && (
                    <Chip
                      variant="outlined"
                      label={checkIsGraded() ? 'Graded' : 'Pending'}
                      style={labelStyles}
                      color={checkIsGraded() ? 'success' : 'warning'}
                    />
                  )}
              </Box>
              <Box
                alignItems="center"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                {QUESTION_NAVIGATION_BUTTONS}
              </Box>
            </Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ mt: '15px' }}
            >
              {(selectedQuestion.status === 'skipped' ||
                !selectedQuestion.openEnded?.answer) && (
                <Chip label="Skipped" style={labelStyles} />
              )}
            </Stack>
            {/* QUESTION DETAILS */}
            <div style={{ padding: '15px 0' }}>
              {selectedQuestion.isOpenEnded && _question.openEnded?.question ? (
                <>
                  <div
                    className="view ql-editor ql-viewer"
                    style={{ marginBottom: '20px' }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: _question.openEnded.question.startsWith('.')
                        ? (_question.openEnded.question || '')
                            .trim()
                            .substring(1)
                        : (_question.openEnded.question || '').trim(),
                    }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    disabled
                    minRows={6}
                    value={selectedQuestion.openEnded?.answer || ''}
                  />
                  {_submission?.status === 'completed' &&
                    authState.user?.role.name === 'Tutor' && (
                      <div style={{ marginTop: '20px' }}>
                        <Typography {...labelProps}>Leave a Comment</Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          size="small"
                          placeholder="Type here..."
                          value={openEndedComment}
                          onChange={(event) =>
                            setOpenEndedComment(event.currentTarget.value)
                          }
                        />
                        <Stack {...gradeSectionStyles}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            style={{ marginRight: '12px' }}
                          >
                            Add Marks
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={openEndedScore}
                            disabled={!selectedQuestion.openEnded?.answer}
                            InputProps={{
                              inputProps: { min: 0 },
                            }}
                            sx={{ width: 75 }}
                            onChange={(event) => {
                              const value = Number(event.currentTarget.value);
                              if (value <= _question.marks) {
                                setOpenEndedScore(value);
                              }
                            }}
                          />
                          <Button
                            style={{ marginLeft: '15px', padding: '6px 20px' }}
                            variant="contained"
                            onClick={handleGradingOpenEndedAnswer}
                          >
                            Save
                          </Button>
                        </Stack>
                      </div>
                    )}
                </>
              ) : (
                <>
                  <div
                    className="view ql-editor ql-viewer"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: _question.details || '',
                    }}
                    style={{ marginBottom: '20px' }}
                  />
                  {/* ANSWER LIST */}
                  <div>
                    <Grid container spacing={2}>
                      {_questionShuffled.map((answer, index) => (
                        <Grid key={index} item xs={12}>
                          <AdminAnswerListItem
                            answer={answer}
                            answers={_questionShuffled}
                            selectedQuestion={selectedQuestion}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                </>
              )}
            </div>
          </Paper>
          {/* MARKING SCHEME */}
          {_submission && _submission.status === 'completed' && (
            <Paper sx={questionDetailContainerStyles}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: '15px' }}
              >
                <Chip label="Marking Scheme" style={labelStyles} />
              </Stack>
              <div>
                <div
                  className="view ql-editor ql-viewer"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: _question.markingSchema || '',
                  }}
                />
              </div>
            </Paper>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <CustomGrayContainer
      style={{
        padding: 0,
        borderRadius: 0,
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      <SubmissionsJumboTron
        grade={topic && topic.grade ? topic.grade : '-'}
        onBack={() => navigate(-1)}
        subject={topic && topic.subject ? topic.subject : ''}
        curriculum={topic && topic.curriculum ? topic.curriculum : '-'}
      />
      <Box style={{ padding: '0 30px 15px 30px' }}>
        <div style={{ marginBottom: '30px' }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ maxWidth: '1300px' }}
          >
            {_user && (
              <>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Student Name"
                    titleProps={titleProps}
                    description={userName}
                    descriptionProps={descriptionProps}
                    avatarProps={{
                      src: _user.avatar,
                      alt: userName,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Last Accessed"
                    titleProps={titleProps}
                    description={lastLogin}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
              </>
            )}
            {_quiz && (
              <>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Topic"
                    titleProps={titleProps}
                    description={topic?.name || '-'}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Sub Topics"
                    titleProps={titleProps}
                    description={subTopicText}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </div>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={4} lg={3}>
              <Box
                sx={{
                  position: { xs: 'static', sm: 'static', md: 'sticky' },
                  top: { xs: 'unset', sm: 'unset', md: '80px' },
                }}
              >
                {QUESTION_DETAILS_VIEW}
              </Box>
            </Grid>
            <Grid item {...detailGridProps}>
              {RENDER_MANAGE_QUESTION_VIEW()}
            </Grid>
          </Grid>
        </div>
      </Box>
    </CustomGrayContainer>
  );
};

export default ViewSubmissionQuestions;
